import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError, API } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import ProspectDrawer from "@/components/ProspectDrawer";
import { ListingBadge } from "@/components/ListingBadge";
import { MailBadge } from "@/components/MailBadge";
import { AddressCheckButton } from "@/components/AddressCheckButton";
import { RefreshDataButton } from "@/components/RefreshDataButton";
import { DncStatus, DncScrubButton } from "@/components/DncScrub";
import { ConsentDialog } from "@/components/ConsentDialog";
import { ImportLeads } from "@/components/ImportLeads";
import { ManualLead } from "@/components/ManualLead";
import { PIPELINE_STAGES, STAGE_BADGE } from "@/components/PipelineStage";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Download, Filter, Users2, ChevronDown, Clock, Send, Loader2 } from "lucide-react";

const money = (n) => (n || n === 0) && !isNaN(n) ? `$${Number(n).toLocaleString()}` : (n || "—");

export function expiryLeft(p, release) {
  if (!release?.enabled || !release?.days || p.last_engaged_at) return null;
  if (["Interested", "Converted", "Do Not Contact", "Not Interested", "Released"].includes(p.status)) return null;
  if (!p.created_at) return null;
  const age = (Date.now() - new Date(p.created_at).getTime()) / 86400000;
  return Math.ceil(release.days - age);
}
export const STATUS_BADGE = {
  "New Prospect": "bg-blue-100 text-blue-800",
  "Email Sent": "bg-indigo-100 text-indigo-800",
  "Voicemail Sent": "bg-purple-100 text-purple-800",
  "Responded": "bg-cyan-100 text-cyan-800",
  "Interested": "bg-emerald-100 text-emerald-800",
  "Converted": "bg-emerald-600 text-white",
  "Not Interested": "bg-slate-200 text-slate-700",
  "Do Not Contact": "bg-red-100 text-red-800",
};

export default function Prospects({ leadsOnly }) {
  const [sp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [state, setState] = useState("");
  const [source, setSource] = useState(sp.get("source") || "");
  const [stage, setStage] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [release, setRelease] = useState({ days: 7, enabled: true });
  const [expiringOnly, setExpiringOnly] = useState(sp.get("expiring") === "1");
  const [bulkPrice, setBulkPrice] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const path = leadsOnly ? "/leads" : "/prospects";
      const params = {};
      if (!leadsOnly) {
        if (search) params.search = search;
        if (status) params.status = status;
        if (state) params.state = state;
        if (source) params.source = source;
        if (stage) params.pipeline_stage = stage;
        if (sp.get("run_id")) params.run_id = sp.get("run_id");
      }
      const { data } = await api.get(path, { params });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, [leadsOnly, search, status, state, source, stage, sp]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/config").then((r) => { setStatuses(r.data.prospect_statuses); setBulkPrice(r.data.bulk_outreach_price); setRelease({ days: r.data.toggles?.lead_release_days || 7, enabled: r.data.toggles?.lead_release_enabled !== false }); }); }, []);

  const [allConsent, setAllConsent] = useState(false);
  const contactAll = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/payments/bulk-checkout", { origin_url: window.location.origin, consent: true });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setBulkBusy(false); }
  };

  const view = expiringOnly ? rows.filter((p) => { const d = expiryLeft(p, release); return d !== null && d <= 2; }) : rows;

  const exportCsv = () => {
    const token = localStorage.getItem("elite_token");
    fetch(`${API}/export/prospects.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = "elite_leads_prospects.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div>
      <PageHeader title={leadsOnly ? "Interested Leads" : "Prospects"}
        subtitle={leadsOnly ? "Prospects who responded or expressed mortgage interest." : "Records generated from your prospecting runs."}>
        {!leadsOnly && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <AddressCheckButton onDone={load} />
            <RefreshDataButton />
            <DncScrubButton path="/prospects/dnc-scrub" onDone={load} testId="dnc-scrub-btn" />
            <ManualLead onDone={load} />
            <ImportLeads onDone={load} />
            <ConsentDialog open={allConsent} onOpenChange={setAllConsent} onConfirm={contactAll} testId="all-consent"
              title="Consent required for Contact all" confirmLabel="Continue to payment"
              statement="I confirm I have the consent required under the TCPA and applicable state law to contact every prospect included in Contact all by email and mail, and to call my imported, inbound and Interested leads using an AI / artificial voice (with an AI voicemail if they don't answer)." />
            <Button onClick={() => setAllConsent(true)} disabled={bulkBusy} className="gold-cta border-0 font-bold" data-testid="contact-all-btn">
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Contact all{bulkPrice ? ` · $${bulkPrice}` : ""}</>}
            </Button>
          </div>
        )}
      </PageHeader>

      {!leadsOnly && (
        <Card className="mb-4 p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="prospect-search"
                placeholder="Search name, address or email…" className="pl-9" />
            </div>
            <Button variant="outline" onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters">
              <Filter className="mr-2 h-4 w-4" /> Filters <ChevronDown className="ml-1 h-4 w-4" />
            </Button>
            <Button onClick={() => setExpiringOnly((v) => !v)} data-testid="toggle-expiring"
              className={expiringOnly ? "bg-amber-500 text-white hover:bg-amber-600" : ""}
              variant={expiringOnly ? "default" : "outline"}>
              <Clock className="mr-2 h-4 w-4" /> Expiring ≤2d
            </Button>
          </div>
          {showFilters && (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status"
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">All statuses</option>
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Input value={state} onChange={(e) => setState(e.target.value.toUpperCase())} placeholder="State (e.g. FL)" data-testid="filter-state-input" />
              <select value={source} onChange={(e) => setSource(e.target.value)} data-testid="filter-source"
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">All lead sources</option>
                <option value="astoria">Online Advertising</option>
                <option value="import">Imported / manual</option>
                <option value="inbound">Inbound calls</option>
              </select>
              <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="filter-stage"
                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">All pipeline stages</option>
                {PIPELINE_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
        </Card>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
        ) : view.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center">
            <Users2 className="h-10 w-10 text-slate-300" />
            <p className="font-semibold text-slate-700">No {expiringOnly ? "leads expiring soon" : (leadsOnly ? "interested leads" : "prospects")} yet</p>
            <p className="max-w-sm text-sm text-slate-500">
              {expiringOnly ? "Nothing is about to release — every active lead has been worked or has time left." : (leadsOnly ? "Leads appear here when a prospect responds or is marked interested." : "Start a prospecting run to populate this list.")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Prospect</th>
                  <th className="px-4 py-3 font-semibold">Location</th>
                  <th className="hidden px-4 py-3 font-semibold md:table-cell">Opportunity</th>
                  <th className="hidden px-4 py-3 font-semibold lg:table-cell">Est. Value</th>
                  <th className="hidden px-4 py-3 font-semibold lg:table-cell">Est. Equity</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="hidden px-4 py-3 font-semibold sm:table-cell">Stage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {view.map((p) => (
                  <tr key={p.id} onClick={() => setSelected(p)} data-testid={`prospect-row-${p.id}`}
                    className="cursor-pointer transition-colors hover:bg-amber-50/40">
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-semibold text-slate-900">{p.name || "Unknown"}</span>
                        {p.recycled && <Badge className="border-0 bg-teal-100 text-teal-800" data-testid={`recycled-${p.id}`}>Recycled</Badge>}
                        {p.source === "astoria" && <Badge className="border-0 bg-indigo-100 text-indigo-800" data-testid={`astoria-${p.id}`}>Astoria</Badge>}
                        <ListingBadge p={p} testId={`listing-badge-${p.id}`} />
                        <MailBadge p={p} testId={`mail-badge-${p.id}`} />
                        {(() => { const d = expiryLeft(p, release); return d !== null ? <Badge className={`border-0 ${d <= 1 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`} data-testid={`expiry-${p.id}`}>{d <= 0 ? "Releasing soon" : `Releases in ${d}d`}</Badge> : null; })()}
                      </div>
                      <div className="text-xs text-slate-500">{p.email || p.phone || "—"}</div>
                      {p.phone && <DncStatus l={p} testId={`dnc-status-${p.id}`} />}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                      <div className="text-xs text-slate-400">{p.property_address}</div>
                    </td>
                    <td className="hidden px-4 py-3 md:table-cell">
                      <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">{p.opportunity_type || "—"}</Badge>
                    </td>
                    <td className="hidden px-4 py-3 text-slate-600 lg:table-cell">{money(p.estimated_property_value)}</td>
                    <td className="hidden px-4 py-3 text-slate-600 lg:table-cell">{money(p.estimated_equity_amount)}</td>
                    <td className="px-4 py-3">
                      <Badge className={`${STATUS_BADGE[p.status] || "bg-slate-100 text-slate-700"} border-0`}>{p.status}</Badge>
                    </td>
                    <td className="hidden px-4 py-3 sm:table-cell">
                      <Badge className={`${STAGE_BADGE[p.pipeline_stage || "New"]} border-0`} data-testid={`stage-${p.id}`}>{p.pipeline_stage || "New"}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <ProspectDrawer prospect={selected} release={release} onClose={() => setSelected(null)} onChange={load} />
    </div>
  );
}
